import type { DateRange } from "../types/trade";

export type StatisticsLocalWindowDeepLink = {
  localStart: string;
  localEnd: string;
  /** IANA zone from the notification URL (optional after consume). */
  timezone: string | null;
  brokerAccountCode: string | null;
};

/** Civil local Date at noon for day-picker display (YYYY-MM-DD). */
export function civilLocalDateFromYmd(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

export function dateRangeFromLocalYmdWindow(
  localStart: string,
  localEnd: string
): DateRange | null {
  const startDate = civilLocalDateFromYmd(localStart);
  const endDate = civilLocalDateFromYmd(localEnd);
  if (!startDate || !endDate) return null;
  if (startDate.getTime() > endDate.getTime()) return null;
  return { startDate, endDate };
}

/**
 * Parse notification / email deep-link query for Statistics.
 * Canonical: ?broker_account_code=&local_start_date=&local_end_date=&timezone=
 */
export function parseStatisticsDeepLinkSearch(
  search: string
): StatisticsLocalWindowDeepLink | null {
  const q = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const localStart = (q.get("local_start_date") || "").trim();
  const localEnd = (q.get("local_end_date") || "").trim();
  const timezone = (q.get("timezone") || "").trim() || null;
  const brokerAccountCode =
    (q.get("broker_account_code") || q.get("account_code") || "").trim() || null;

  if (!localStart || !localEnd) {
    if (!brokerAccountCode) return null;
    return {
      localStart: "",
      localEnd: "",
      timezone,
      brokerAccountCode,
    };
  }

  if (!/^\d{4}-\d{2}-\d{2}$/.test(localStart) || !/^\d{4}-\d{2}-\d{2}$/.test(localEnd)) {
    return null;
  }

  return {
    localStart,
    localEnd,
    timezone,
    brokerAccountCode,
  };
}

/** Drop one-shot window params after applying them to calendar state. Keeps broker. */
export function stripStatisticsDeepLinkWindowParams(search: string): string {
  const q = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  q.delete("local_start_date");
  q.delete("local_end_date");
  q.delete("timezone");
  const next = q.toString();
  return next ? `?${next}` : "";
}
